import { Calculator } from "lucide-react";
import { getProductCondition } from "@/lib/productCondition";
import FieldClear from "@/components/FieldClear";

interface SoslineRow {
  condition: number;
  km: number;
}

interface PriceCalculatorProps {
  lunatikPrice: string;
  onLunatikPriceChange: (value: string) => void;
  suggestedSosline: number | null;
  soslineTable: SoslineRow[] | null;
  condition: number;
  onApplySosline: () => void;
}

export default function PriceCalculator({
  lunatikPrice,
  onLunatikPriceChange,
  suggestedSosline,
  soslineTable,
  condition,
  onApplySosline,
}: PriceCalculatorProps) {
  return (
    <div className="space-y-3 pb-4 border-b border-border">
      <div className="flex items-center gap-2">
        <div className="size-6 rounded-lg bg-amber-50 flex items-center justify-center">
          <Calculator className="size-3.5 text-amber-700" />
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Kalkulator (Lunatik → Sosline)
        </span>
      </div>
      <div>
        <label htmlFor="lunatikPrice" className="text-xs text-muted-foreground mb-1 block">
          Lunatik prodajna cijena
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              id="lunatikPrice"
              type="number"
              min="0"
              step="0.01"
              value={lunatikPrice}
              onChange={(e) => onLunatikPriceChange(e.target.value)}
              placeholder="npr. 10.00"
              className={`w-full h-11 rounded-xl border border-border bg-neutral-50 pl-3 text-sm font-semibold tabular-nums placeholder:text-neutral-300 focus:ring-2 focus:ring-foreground/10 focus:border-foreground focus:bg-white outline-none transition ${
                lunatikPrice.trim() ? "pr-16" : "pr-9"
              }`}
            />
            <FieldClear
              show={lunatikPrice.trim() !== ""}
              onClear={() => onLunatikPriceChange("")}
              className="right-7"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-medium text-neutral-400 pointer-events-none z-0">
              KM
            </span>
          </div>
          {suggestedSosline != null && (
            <button
              type="button"
              onClick={onApplySosline}
              className="shrink-0 h-11 px-3 rounded-xl bg-foreground text-white text-xs font-semibold hover:bg-foreground/90 active:scale-[0.98] transition"
            >
              Postavi cijenu
            </button>
          )}
        </div>
      </div>
      {lunatikPrice.trim() !== "" && suggestedSosline == null && (
        <p className="text-[11px] text-amber-700 font-medium">Unesite ispravan broj ≥ 0.</p>
      )}
      {suggestedSosline != null && (
        <div className="rounded-xl bg-amber-50/80 border border-amber-100 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wider text-amber-800/80 font-semibold mb-0.5">
            {`Sosline prodajna cijena (${getProductCondition(condition)?.shortName ?? `stepen ${condition}`})`}
          </div>
          <div className="text-lg font-bold tabular-nums text-amber-950">
            {suggestedSosline.toFixed(2).replace(".", ",")} KM
          </div>
        </div>
      )}
      {soslineTable && (
        <div className="overflow-hidden rounded-lg border border-border/80 text-xs">
          <div className="grid grid-cols-3 bg-neutral-100 px-2 py-1.5 font-semibold text-muted-foreground text-[10px]">
            <span>Stepen</span>
            <span className="min-w-0 truncate">Stanje oštećenja</span>
            <span className="text-right">Sosline</span>
          </div>
          {soslineTable.map((row) => {
            const d = getProductCondition(row.condition);
            return (
            <div
              key={row.condition}
              className={`grid grid-cols-3 gap-1 px-2 py-1.5 tabular-nums text-[11px] border-t border-border/60 items-center ${
                row.condition === condition ? "bg-amber-50/90 font-semibold" : "bg-white"
              }`}
            >
              <span>{row.condition}</span>
              <span className="min-w-0 truncate text-left" title={d?.description}>
                {d?.shortName ?? "—"}
              </span>
              <span className="text-right">
                {row.km.toFixed(2).replace(".", ",")} KM
              </span>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
